import { useMutation } from '@tanstack/react-query'
import type {
  CompleteImageUploadData,
  CreateImageUploadData,
  Options,
} from '@workspace/api-backend'
import {
  completeImageUploadMutation,
  createImageUploadMutation,
} from '@workspace/api-backend/react-query'
import { useState } from 'react'

interface UseImageUploadOptions {
  onSuccess?: (url: string) => void
  onError?: (error: Error) => void
}

export function useImageUpload(options?: UseImageUploadOptions) {
  const [isUploading, setIsUploading] = useState(false)

  const createMut = useMutation({ ...createImageUploadMutation() })
  const completeMut = useMutation({ ...completeImageUploadMutation() })

  const upload = async (file: File): Promise<string> => {
    setIsUploading(true)
    try {
      const createResponse = await createMut.mutateAsync({
        body: { fileName: file.name },
      } as Options<CreateImageUploadData>)

      if (createResponse.status !== 'ok') throw new Error('Failed to create upload URL')

      const { key, uploadUrl, uploadFields } = createResponse.data

      const formData = new FormData()
      for (const [fieldKey, fieldValue] of Object.entries(uploadFields)) {
        formData.append(fieldKey, fieldValue)
      }
      formData.append('Content-Type', file.type)
      formData.append('file', file)

      const uploadResponse = await fetch(uploadUrl, { method: 'POST', body: formData })
      if (!uploadResponse.ok) throw new Error('Failed to upload file to storage')

      const completeResponse = await completeMut.mutateAsync({
        path: { key: encodeURIComponent(key) },
      } as Options<CompleteImageUploadData>)

      if (completeResponse.status !== 'ok') throw new Error('Failed to complete upload')

      const url = completeResponse.data.url
      options?.onSuccess?.(url)
      return url
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Upload failed')
      options?.onError?.(err)
      throw err
    } finally {
      setIsUploading(false)
    }
  }

  return { upload, isUploading }
}
